import { Fragment, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import HRMSApi from "../../api/HRMSApi";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import { Col, Container, Row } from "react-bootstrap";

const AssignTeamModal = ({
    addProject,
    showModal,
    reloadComponent,
    setReloadComponent,
    setShowModal
}) => {

    const [projectOptions, setProjectOptions] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [showNewProjectInput, setShowNewProjectInput] = useState(false);
    const [newProjectName, setNewProjectName] = useState("");
    const [assignTeamLeader, setAssignTeamLoader] = useState(false);

    const [employeeIdsWithImage, setEmployeeIdsWithImage] = useState([]);

    const fetchListOfProjects = async () => {
        const responseData = await HRMSApi.getListOfProject();
        const activeProjects = responseData?.records?.filter(p => p.Active__c);
        const formattedOptions = activeProjects?.map(p => ({
            value: p.Id,
            label: p.Name
        }));
        setProjectOptions(formattedOptions || []);
    };

    const fetchAllUsers = async () => {
        try {
            const result = await HRMSApi.getAllUsers();

            if (!result?.success || !Array.isArray(result.record)) return;

            const emailList = result.record
                .map(user => user.empEmail)
                .filter(Boolean);

            let emailToContactMap = {};

            try {
                const contactResult = await HRMSApi.findUserByEmail({ emails: emailList });
                if (contactResult?.success && contactResult.records) {
                    if (Array.isArray(contactResult.records)) {
                        emailToContactMap = contactResult.records.reduce((acc, item) => {
                            acc[item.Email] = item.Id;
                            return acc;
                        }, {});
                    } else if (typeof contactResult.records === "object") {
                        emailToContactMap = Object.fromEntries(
                            Object.entries(contactResult.records)
                        );
                    }
                }

            } catch (err) {
                console.error("Error fetching contacts by email:", err);
            }

            const usersWithIds = result.record.map(user => ({
                value: emailToContactMap[user.empEmail] || null,
                label: user.empName,
                email: user.empEmail,
                profileURL: user.profileURL,
                contactId: emailToContactMap[user.empEmail] || null
            }));

            setEmployeeIdsWithImage(
                usersWithIds.map(item => ({
                    contactId: item.contactId,
                    profileURL: item.profileURL
                }))
            );

            const uniqueUsers = [
                ...new Map(usersWithIds.map(u => [u.email, u])).values()
            ];

            setUsersList(uniqueUsers);

        } catch (err) {
            console.error("Error fetching users:", err);
        }
    };


    useEffect(() => {
        setSelectedProject(null);
        setSelectedUsers([]);
        fetchListOfProjects();
        fetchAllUsers();
    }, [reloadComponent, showModal]);

    const handleUserSelection = (selected) => {
        setSelectedUsers(selected);
    };

    const formatOptionLabel = option => (
        <div style={{ display: "flex", alignItems: "center" }}>
            <img
                src={option.profileURL || "default_emp_profile.png"}
                alt="user"
                className="rounded-circle me-2"
                style={{ width: 18, height: 18, objectFit: "cover" }}
            />
            <span style={{ fontSize: "13px" }}>{option.label}</span>
        </div>
    );

    const customSelectStyles = {
        control: base => ({
            ...base,
            borderRadius: "8px",
            minHeight: "38px",
            borderColor: "#d0d0d0",
            boxShadow: "none",
            "&:hover": { borderColor: "#a8a8a8" }
        }),
        multiValue: base => ({
            ...base,
            backgroundColor: "#eef4ff",
            borderRadius: "8px",
            padding: "2px 4px"
        }),
        multiValueLabel: base => ({
            ...base,
            fontSize: "12px",
            paddingLeft: 0
        }),
        option: base => ({
            ...base,
            padding: "6px 10px",
            fontSize: "13px",
            cursor: "pointer"
        })
    };

    const handleAddNewProject = () => {
        if (!newProjectName.trim()) {
            toast.error("Please enter project name", { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        const newProject = {
            value: `new-${newProjectName}`,
            label: newProjectName
        };

        setProjectOptions(prev => [...prev, newProject]);
        setSelectedProject(newProject);
        setSelectedUsers([]);
        setNewProjectName("");
        setShowNewProjectInput(false);
    };

    const handleSave = async () => {

        if (isSaving) return; 

        if (!selectedProject) {
            toast.error("Please select a project", { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        if (!selectedUsers.length) {
            toast.error("Please assign at least one employee", { position: toast.POSITION.TOP_RIGHT });
            return;
        }

        const loadingToast = toast.loading("Processing...", {
            position: toast.POSITION.TOP_RIGHT
        });

        try {
            setIsSaving(true); 

            let projectId = selectedProject.value;

            /* ---------- CREATE PROJECT ---------- */
            if (typeof projectId === "string" && projectId.includes("new")) {

                const createProjectRes = await HRMSApi.createProject({
                    name: selectedProject.label,
                    active: true
                });

                if (!createProjectRes?.success) {
                    toast.update(loadingToast, {
                        render: createProjectRes?.message || "Failed to create project",
                        type: "error",
                        isLoading: false,
                        autoClose: 3000
                    });
                    return;
                }

                if (
                    createProjectRes?.records?.message ===
                    "Project with the same name already exists."
                ) {
                    toast.update(loadingToast, {
                        render: "Project already exists with same name.",
                        type: "error",
                        isLoading: false,
                        autoClose: 3000
                    });
                    return;
                }

                projectId = createProjectRes?.records?.projectId;
            }

            /* ---------- ASSIGN TEAM ---------- */
            const payload = {
                projectId,
                employeeIds: selectedUsers.map(u => u.contactId)
            };

            const responseBody = await HRMSApi.createTeamAssignment(payload);

            if (responseBody?.success) {
                toast.update(loadingToast, {
                    render: "Team assigned successfully!",
                    type: "success",
                    isLoading: false,
                    autoClose: 3000
                });
                setShowModal(false);
            } else {
                toast.update(loadingToast, {
                    render: responseBody?.message || "Failed to assign team",
                    type: "error",
                    isLoading: false,
                    autoClose: 3000
                });
            }

            setReloadComponent(prev => !prev);

        } catch (error) {
            toast.update(loadingToast, {
                render: "Something went wrong while assigning the team",
                type: "error",
                isLoading: false,
                autoClose: 3000
            });
            console.error("Assignment Error:", error);
        } finally {
            setIsSaving(false); 
        }
    };

    const availableUsers = usersList.filter(
        user => !selectedUsers.some(sel => sel.contactId === user.contactId)
    );

    const handleChangeProject = async (item) => {
        if (!item) {
            setSelectedProject(null);
            setSelectedUsers([]);
            return;
        }

        setSelectedProject(item);
        setAssignTeamLoader(true);

        try {
            const projectDetails = await HRMSApi.getTeamProjects(item.value);
            const imageList = Array.isArray(employeeIdsWithImage) ? employeeIdsWithImage : [];

            if (projectDetails?.records?.employeeList?.length) {
                const preselectedUsers = projectDetails.records.employeeList.map(emp => {
                    const image = imageList.find(img => img.contactId === emp.id);

                    return {
                        value: emp.id,
                        label: emp.name,
                        contactId: emp.id,
                        profileURL: image?.profileURL || "default_emp_profile.png"
                    };
                });

                const filteredPreselectedUsers = preselectedUsers.filter(
                    u => usersList.some(user => user.contactId === u.contactId)
                );
                setSelectedUsers(filteredPreselectedUsers);
            } else {
                setSelectedUsers([]);
            }
        } catch (error) {
            console.error("Error fetching project details:", error);
            setSelectedUsers([]);
        } finally {
            setAssignTeamLoader(false);
        }
    };

    return (
        <Fragment>
            <Modal size="lg" show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header>
                    <Modal.Title className="fw-bold">Assign Team</Modal.Title>
                </Modal.Header>

                <Modal.Body className="px-4">

                    <div className="mb-4">
                        <Row className="mb-2">
                            <Col sm={10}>
                                <label className="form-label fw-semibold">
                                    Select Project <span style={{ color: "red" }}>*</span>
                                </label>
                            </Col>
                            {
                                addProject &&
                                <Col xs={2}>
                                    <button
                                        type="button"
                                        className={`btn ${showNewProjectInput ? 'btn-danger' : 'btn-success'} w-100 d-flex justify-content-center align-items-center`}
                                        onClick={() => { setShowNewProjectInput(!showNewProjectInput); setSelectedProject(null); setSelectedUsers([]) }}
                                        aria-pressed={showNewProjectInput}
                                        aria-label={showNewProjectInput ? "Close new project input" : "Add new project"}
                                    >
                                        {showNewProjectInput ? "✕ Close" : "+ Add Project"}
                                    </button>

                                </Col>
                            }
                        </Row>
                        <Row className="align-items-center">
                            <Col xs={12}>
                                <Select
                                    options={projectOptions}
                                    value={selectedProject}
                                    onChange={handleChangeProject}
                                    placeholder="Select project..."
                                    styles={customSelectStyles}
                                    isClearable
                                    name="project"
                                />
                            </Col>
                        </Row>


                        {showNewProjectInput && (
                            <div className="mt-3 p-3 border rounded bg-light">
                                <Row className="align-items-center">
                                    <Col xs={9}>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Enter new project name"
                                            value={newProjectName}
                                            onChange={(e) => setNewProjectName(e.target.value)}
                                            autoFocus
                                        />
                                    </Col>
                                    <Col xs={3} className="d-flex justify-content-end ">
                                        <button
                                            className="btn btn-primary w-75"
                                            onClick={handleAddNewProject}
                                            disabled={!newProjectName.trim()}
                                        >
                                            Add
                                        </button>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>

                    {
                        assignTeamLeader ? <Container>
                            <div>
                                <div className="sk-cube-grid">
                                    <div className="sk-cube sk-cube1"></div>
                                    <div className="sk-cube sk-cube2"></div>
                                    <div className="sk-cube sk-cube3"></div>
                                    <div className="sk-cube sk-cube4"></div>
                                    <div className="sk-cube sk-cube5"></div>
                                    <div className="sk-cube sk-cube6"></div>
                                    <div className="sk-cube sk-cube7"></div>
                                    <div className="sk-cube sk-cube8"></div>
                                    <div className="sk-cube sk-cube9"></div>
                                </div>
                            </div>
                        </Container> : (!showNewProjectInput &&
                            <div className="mb-2">
                                <label className="form-label fw-semibold">
                                    Assign Employees <span style={{ color: "red" }}>*</span>
                                </label>
                                <Select
                                    isMulti
                                    options={availableUsers}            // users not already assigned
                                    value={selectedUsers}               // users already assigned
                                    onChange={handleUserSelection}
                                    placeholder={selectedProject ? "Select employees..." : "Select a project first..."}
                                    styles={customSelectStyles}
                                    formatOptionLabel={formatOptionLabel}
                                    closeMenuOnSelect={false}
                                    isDisabled={!selectedProject}
                                />


                            </div>)
                    }
                </Modal.Body>

                <Modal.Footer>
                    <button className="btn btn-outline-secondary" onClick={() => { setShowModal(false); }}>
                        Close
                    </button>
                    <button className="btn btn-primary" onClick={handleSave}>
                        Save
                    </button>
                </Modal.Footer>
            </Modal>
            <ToastContainer />
        </Fragment>
    );
};

export default AssignTeamModal;